import { useEffect, useMemo, useRef, useState } from 'react';
import { useWorklog } from '../state/WorklogContext';
import { useStatus } from '../state/StatusContext';
import { useUI } from '../state/UIContext';
import { deriveTasksFromItems, itemPrefix } from '../worklog/groups';
import { ALL_EVENTS, scopeItemsToContract } from '../worklog/types';
import type { Item, SnapshotItem } from '../worklog/types';

interface SidebarProps {
  activeTaskId: string;
  onNavigate: (id: string) => void;
}

// Convert snapshot items (shape from /api/work/snapshot) into the Item shape the
// existing groups + scope helpers expect. SnapshotItem has the same
// canonical fields plus actual_seconds_derived; Item uses actual_seconds.
function snapshotItemToItem(it: SnapshotItem): Item {
  return {
    id: it.id,
    label: it.label,
    status: it.status,
    block_reason: it.block_reason,
    estimate_minutes: it.estimate_minutes ?? null,
    priority: it.priority ?? null,
    actual_seconds: it.actual_seconds_derived,
    contract: it.contract,
    created_at: it.created_at,
    completed_at: it.completed_at ?? null,
    last_used_at: it.last_used_at ?? null,
  } as Item;
}

export function Sidebar({ activeTaskId, onNavigate }: SidebarProps) {
  const { state, setContract } = useWorklog();
  const { snapshot } = useStatus();
  const { theme, toggleTheme, navMode, setNavMode } = useUI();

  // Build contract dropdown from snapshot. Always include the ALL_EVENTS
  // sentinel at the top; preserve the active contract even if not in
  // snapshot (e.g., race conditions during contract-start).
  const contracts = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [ALL_EVENTS];
    seen.add(ALL_EVENTS);
    const fromSnap = (snapshot?.contracts ?? [])
      .filter(c => c.name !== 'all_events')
      .map(c => c.name);
    for (const c of fromSnap) {
      if (!seen.has(c)) { seen.add(c); out.push(c); }
    }
    if (!seen.has(state.activeContract)) out.push(state.activeContract);
    return out;
  }, [snapshot, state.activeContract]);

  // Items come from the snapshot, scoped to the active contract.
  const snapItems: Item[] = useMemo(() => {
    const all = (snapshot?.items ?? []).map(snapshotItemToItem);
    return scopeItemsToContract(all, state.activeContract);
  }, [snapshot, state.activeContract]);
  const tasks = useMemo(() => deriveTasksFromItems(snapItems), [snapItems]);

  // Per-contract progress aggregates come from snapshot's active contract.
  const contractKey = state.activeContract === ALL_EVENTS ? 'all_events' : state.activeContract;
  const activeContractData = snapshot?.contracts.find(c => c.name === contractKey)
    ?? snapshot?.contracts.find(c => c.active)
    ?? null;

  return (
    <nav className="sidebar">
      <h1 onClick={() => onNavigate('overview')} title="Back to overview">WORK</h1>

      <ContractSelector
        contracts={contracts}
        active={state.activeContract}
        onChange={setContract}
      />

      <ProgressSummary
        done={activeContractData?.status_counts.done ?? 0}
        total={activeContractData?.status_counts.total ?? 0}
        cache={state.worklog.cache}
        eventCount={state.worklog.events.length}
        error={state.worklog.error}
      />

      <div className="nav-mode-toggle">
        <button
          type="button"
          className={'nm-btn' + (navMode === 'category' ? ' active' : '')}
          onClick={() => setNavMode('category')}
        >
          Category
        </button>
        <button
          type="button"
          className={'nm-btn' + (navMode === 'priority' ? ' active' : '')}
          onClick={() => setNavMode('priority')}
        >
          Priority
        </button>
      </div>

      <TaskNav
        items={snapItems}
        tasks={tasks}
        activeTaskId={activeTaskId}
        navMode={navMode}
        onNavigate={onNavigate}
      />

      <div className="sidebar-foot">
        <div className="theme-toggle">
          <span className="label">Theme</span>
          <button
            className="theme-switch"
            type="button"
            aria-label={'Toggle theme (currently ' + theme + ')'}
            onClick={toggleTheme}
          >
            <span className="track-icons" aria-hidden="true">
              <SunIcon />
              <MoonIcon />
            </span>
            <span className="knob">
              <SunIcon className="icon-sun" weight={2.2} />
              <MoonIcon className="icon-moon" weight={2.2} />
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
}

function ContractSelector({
  contracts,
  active,
  onChange,
}: {
  contracts: string[];
  active: string;
  onChange: (c: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(ev: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const display = active === ALL_EVENTS ? 'All events' : active;

  return (
    <div className="contract-selector">
      <label>Active contract</label>
      <div className="cs-wrap" ref={wrapRef} data-open={open ? 'true' : 'false'}>
        <button
          type="button"
          className="cs-button"
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{display}</span>
          <span className="cs-arrow">{'▾'}</span>
        </button>
        <ul className="cs-popup" role="listbox">
          {contracts.map(c => {
            const label = c === ALL_EVENTS ? 'All events' : c;
            const isAll = c === ALL_EVENTS;
            return (
              <li
                key={c}
                role="option"
                aria-selected={c === active}
                className="cs-option"
                data-selected={c === active ? 'true' : 'false'}
                onClick={() => { onChange(c); setOpen(false); }}
              >
                <span>{label}</span>
                {isAll ? <span className="cs-tag">all</span> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ProgressSummary({
  done,
  total,
  cache,
  eventCount,
  error,
}: {
  done: number;
  total: number;
  cache: string;
  eventCount: number;
  error: string | null;
}) {
  const pct = total ? Math.round((done / total) * 100) : 0;

  let statusLabel: string;
  if (cache === 'loading') statusLabel = 'loading...';
  else if (cache === 'error') statusLabel = 'error: ' + (error || 'unknown');
  else statusLabel = eventCount + ' events (' + cache + ')';

  return (
    <div className="progress-summary">
      <div>Checklist progress</div>
      <div className="progress-bar"><div className="fill" style={{ width: pct + '%' }} /></div>
      <div className="pct">{pct}% ({done}/{total})</div>
      <div className="worklog-status">
        <span className={'dot ' + cache} /> worklog: {statusLabel}
      </div>
    </div>
  );
}

function TaskNav({
  items,
  tasks,
  activeTaskId,
  navMode,
  onNavigate,
}: {
  items: Item[];
  tasks: ReturnType<typeof deriveTasksFromItems>;
  activeTaskId: string;
  navMode: 'category' | 'priority';
  onNavigate: (id: string) => void;
}) {
  if (navMode === 'priority') {
    return (
      <ul>
        <li>
          <a
            data-id="overview"
            className={activeTaskId === 'overview' ? 'active' : ''}
            onClick={() => onNavigate('overview')}
          >
            Overview
          </a>
        </li>
        {items.slice(0, 60).map(it => {
          const id = 'item-' + it.id;
          const cls = [it.status === 'done' || it.status === 'dropped' ? 'complete' : '',
                       it.status === 'blocked' ? 'blocked' : '',
                       activeTaskId === id ? 'active' : ''].filter(Boolean).join(' ');
          return (
            <li key={id}>
              <a className={cls} data-id={id} onClick={() => onNavigate(id)}>
                <span className="task-prio-num">{typeof it.priority === 'number' ? it.priority : ''}</span>
                {it.label || it.id}
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul>
      {tasks.map(t => {
        const groupItems = t.items || [];
        const doneCount = groupItems.filter(i => i.done).length;
        const totalCount = groupItems.length;
        const blocked = groupItems.some(i => i.blocked);
        const allDone = totalCount > 0 && doneCount === totalCount;
        const cls = [
          t.id === activeTaskId ? 'active' : '',
          allDone ? 'complete' : '',
          blocked ? 'blocked' : '',
        ].filter(Boolean).join(' ');
        const showProgress = !t.isOverview && totalCount > 0;
        return (
          <li key={t.id}>
            <a className={cls} data-id={t.id} onClick={() => onNavigate(t.id)}>
              {t.title}
              {showProgress
                ? <span className="task-progress">{doneCount}/{totalCount}</span>
                : null}
            </a>
            {/* sub-items only shown on hover/expanded view in future; keep flat for now */}
            {!t.isOverview && groupItems.length > 0
              ? null
              : null}
            {!t.isOverview && groupItems.length > 0
              ? null
              : null}
            {!t.isOverview && groupItems.length > 0
              ? null
              : null}
            {!t.isOverview && groupItems.length > 0 && t.id === activeTaskId
              ? <SubNav items={groupItems} onNavigate={onNavigate} activeTaskId={activeTaskId} />
              : null}
          </li>
        );
      })}
    </ul>
  );
}

function SubNav({
  items,
  onNavigate,
  activeTaskId,
}: {
  items: NonNullable<ReturnType<typeof deriveTasksFromItems>[number]['items']>;
  onNavigate: (id: string) => void;
  activeTaskId: string;
}) {
  return (
    <ul style={{ paddingLeft: 12 }}>
      {items.map(it => {
        const id = 'item-' + it.id;
        const cls = [
          activeTaskId === id ? 'active' : '',
          it.done ? 'complete' : '',
          it.blocked ? 'blocked' : '',
        ].filter(Boolean).join(' ');
        return (
          <li key={id}>
            <a className={cls} data-id={id} onClick={() => onNavigate(id)}>
              <span className="task-prio-num">{itemPrefix(it.id).slice(0, 3)}</span>
              {it.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function SunIcon({ className, weight = 2 }: { className?: string; weight?: number } = {}) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={weight} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className, weight = 2 }: { className?: string; weight?: number } = {}) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={weight} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
